import { Fragment, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type {
	SmartField_Api_Contracts_Site_SiteDto as SiteDto,
	SmartField_Api_Features_DataWarehouse_DataExport_SiteHierarchyDto as SiteHierarchyDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchyPlotDto as HierarchyPlotDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchyFieldDto as HierarchyFieldDto,
	SmartField_Api_Features_DataWarehouse_DataExport_CoordinateDto as ExportCoordinateDto,
} from '../../../api';
import { PlotTooltip } from '../../shared/PlotTooltip';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
	iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
	shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

type PlotCapability = 'chamber' | 'tower' | 'sensor' | 'manual' | 'mixed' | 'none';

function getPlotCapability(plot: HierarchyPlotDto): PlotCapability {
	const kinds: PlotCapability[] = [];
	if (plot.hasChamberFlux) kinds.push('chamber');
	if (plot.hasGradientFlux) kinds.push('tower');
	if ((plot.sensors ?? []).length > 0) kinds.push('sensor');
	if (plot.hasManualObservations) kinds.push('manual');
	if (kinds.length > 1) return 'mixed';
	if (kinds.length === 1) return kinds[0]!;
	return 'none';
}

const CAPABILITY_COLORS: Record<PlotCapability, { bg: string; border: string; label: string }> = {
	chamber: { bg: '#22c55e', border: '#16a34a', label: 'Chamber flux' },
	tower:   { bg: '#3b82f6', border: '#2563eb', label: 'Tower flux' },
	sensor:  { bg: '#f59e0b', border: '#d97706', label: 'Sensor' },
	manual:  { bg: '#9ca3af', border: '#6b7280', label: 'Manual observations' },
	mixed:   { bg: '#8b5cf6', border: '#374151', label: 'Mixed data' },
	none:    { bg: '#e5e7eb', border: '#d1d5db', label: 'No data' },
};

function getPlotCapabilityDetail(plot: HierarchyPlotDto): string {
	const parts: string[] = [];
	if (plot.hasChamberFlux) { parts.push('Chamber flux'); }
	if (plot.hasGradientFlux) { parts.push('Tower flux'); }
	if ((plot.sensors ?? []).length > 0) { parts.push('Sensors'); }
	if (plot.hasManualObservations) { parts.push('Manual obs'); }
	return parts.join(' · ');
}

function createSiteIcon(selected: boolean) {
	return L.divIcon({
		className: '',
		html: `<div style="
			width: 16px; height: 16px;
			border-radius: 50%;
			background: ${selected ? '#0d9488' : '#6b7280'};
			border: 2px solid ${selected ? '#0f766e' : '#4b5563'};
			box-shadow: 0 1px 4px rgba(0,0,0,0.4);
		"></div>`,
		iconSize: [16, 16],
		iconAnchor: [8, 8],
	});
}

function getMixedConicGradient(plot: HierarchyPlotDto): string {
	const parts: string[] = [];
	if (plot.hasChamberFlux) { parts.push(CAPABILITY_COLORS.chamber.bg); }
	if (plot.hasGradientFlux) { parts.push(CAPABILITY_COLORS.tower.bg); }
	if ((plot.sensors ?? []).length > 0) { parts.push(CAPABILITY_COLORS.sensor.bg); }
	if (plot.hasManualObservations) { parts.push(CAPABILITY_COLORS.manual.bg); }
	const n = parts.length;
	const stops = parts.map((c, i) => `${c} ${Math.round((i / n) * 100)}% ${Math.round(((i + 1) / n) * 100)}%`);
	return `conic-gradient(${stops.join(', ')})`;
}

function createPlotIcon(plot: HierarchyPlotDto, selected: boolean) {
	const cap = getPlotCapability(plot);
	const colors = CAPABILITY_COLORS[cap];
	const size = selected ? 16 : 12;
	const bg = cap === 'mixed' ? getMixedConicGradient(plot) : colors.bg;
	const border = cap === 'mixed' ? 'rgba(0,0,0,0.25)' : (selected ? colors.border : colors.border + 'aa');
	return L.divIcon({
		className: '',
		html: `<div style="
			width: ${size}px; height: ${size}px;
			border-radius: 50%;
			background: ${bg};
			border: 2px solid ${border};
			box-shadow: 0 1px 3px rgba(0,0,0,0.35);
			opacity: ${selected ? 1 : 0.8};
		"></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
	});
}

function centroid(coords: ExportCoordinateDto[]): [number, number] | null {
	const valid = coords.filter(c => c.latitude != null && c.longitude != null);
	if (valid.length === 0) {
		return null;
	}
	const lat = valid.reduce((s, c) => s + c.latitude!, 0) / valid.length;
	const lng = valid.reduce((s, c) => s + c.longitude!, 0) / valid.length;
	return [lat, lng];
}

function MapCenterUpdater({ site }: { site: SiteDto | null }) {
	const map = useMap();

	useEffect(() => {
		if (site?.location) {
			map.setView([site.location.y, site.location.x], 13);
		}
	}, [site, map]);

	return null;
}

function ZoomTracker({ onZoomChange }: { onZoomChange: (zoom: number) => void }) {
	useMapEvents({
		zoomend: (e) => onZoomChange(e.target.getZoom()),
	});
	return null;
}

function SplitDot({ colors }: { colors: string[] }) {
	const n = colors.length;
	const r = 6;
	const cx = r;
	const cy = r;
	const segments = colors.map((color, i) => {
		const startAngle = (i / n) * 2 * Math.PI - Math.PI / 2;
		const endAngle = ((i + 1) / n) * 2 * Math.PI - Math.PI / 2;
		const x1 = cx + r * Math.cos(startAngle);
		const y1 = cy + r * Math.sin(startAngle);
		const x2 = cx + r * Math.cos(endAngle);
		const y2 = cy + r * Math.sin(endAngle);
		const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
		return (
			<path
				key={i}
				d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
				fill={color}
			/>
		);
	});
	return (
		<svg width={r * 2} height={r * 2} viewBox={`0 0 ${r * 2} ${r * 2}`} className="flex-shrink-0">
			{segments}
			<circle cx={cx} cy={cy} r={r - 0.5} fill="none" stroke="#7c3aed" strokeWidth="1" />
		</svg>
	);
}

function MapLegend() {
	const entries = Object.entries(CAPABILITY_COLORS).filter(([k]) => k !== 'none') as [PlotCapability, typeof CAPABILITY_COLORS[PlotCapability]][];
	return (
		<div className="absolute bottom-3 left-3 z-[400] bg-white/90 backdrop-blur-sm rounded-lg shadow px-3 py-2 text-xs space-y-1 border border-gray-200">
			{entries.map(([key, colors]) => (
				<div key={colors.label} className="flex items-center gap-1.5">
					{key === 'mixed' ? (
						<SplitDot colors={[
							CAPABILITY_COLORS.chamber.bg,
							CAPABILITY_COLORS.tower.bg,
							CAPABILITY_COLORS.sensor.bg,
							CAPABILITY_COLORS.manual.bg,
						]} />
					) : (
						<span style={{ background: colors.bg, borderColor: colors.border }} className="w-3 h-3 rounded-full border-2 flex-shrink-0" />
					)}
					<span className="text-gray-700">{key === 'mixed' ? 'Multiple types' : colors.label}</span>
				</div>
			))}
		</div>
	);
}

type ExportSiteMapProps = {
	sites: SiteDto[];
	selectedSiteSerialNumber: string | null;
	selectedLocationIds: Set<number>;
	hierarchy: SiteHierarchyDto | undefined;
	onSiteSelect: (serialNumber: string) => void;
	onPlotSelect: (plotId: number) => void;
};

export function ExportSiteMap({
	sites,
	selectedSiteSerialNumber,
	selectedLocationIds,
	hierarchy,
	onSiteSelect,
	onPlotSelect,
}: ExportSiteMapProps) {
	const selectedSite = sites.find(s => s.serialNumber === selectedSiteSerialNumber) ?? null;
	const [zoom, setZoom] = useState(7);

	type HierarchyPlot = HierarchyPlotDto;
	const plotEntriesMap = new Map<number, { plot: HierarchyPlot; fieldName: string; treatmentDescription: string; treatmentFriendlyName: string | null }>();
	for (const field of hierarchy?.fields ?? []) {
		for (const treatment of field.treatments ?? []) {
			for (const plot of treatment.plots ?? []) {
				if (plot.locationId != null && plot.geolocation && plot.geolocation.length > 0) {
					plotEntriesMap.set(plot.locationId, {
						plot,
						fieldName: field.fieldName ?? '',
						treatmentDescription: treatment.treatmentDescription ?? '',
						treatmentFriendlyName: treatment.treatmentFriendlyName ?? null,
					});
				}
			}
		}
	}
	const plotEntries = Array.from(plotEntriesMap.values());

	const fieldPolygons: { field: HierarchyFieldDto; coords: ExportCoordinateDto[] }[] = [];
	for (const field of hierarchy?.fields ?? []) {
		if (field.geolocation) {
			const validCoords = field.geolocation.filter(
				c => isFinite(c.latitude ?? NaN) && isFinite(c.longitude ?? NaN),
			);
			if (validCoords.length >= 3) {
				fieldPolygons.push({ field, coords: validCoords });
			}
		}
	}

	return (
		<div className="relative h-96 rounded-lg overflow-hidden border border-gray-200">
			<MapContainer
				center={[56.0, 10.5]}
				zoom={7}
				style={{ height: '100%', width: '100%' }}
				scrollWheelZoom={true}
			>
				<TileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
					maxZoom={20}
					maxNativeZoom={19}
				/>
				<MapCenterUpdater site={selectedSite} />
				<ZoomTracker onZoomChange={setZoom} />

				{sites.map(site => {
					if (!site.location && (!site.shapeCoordinates || site.shapeCoordinates.length === 0)) {
						return null;
					}
					const isSelected = site.serialNumber === selectedSiteSerialNumber;

					const tooltip = (
						<Tooltip sticky>
							<span className="font-medium">{site.name}</span>
							<br />
							<span className="text-gray-500 text-xs">{site.serialNumber}</span>
						</Tooltip>
					);

					return (
						<Fragment key={`site-${site.id}`}>
							{site.shapeCoordinates && site.shapeCoordinates.length > 0 && (
								<Polygon
									positions={site.shapeCoordinates.map(c => [c.y, c.x] as [number, number])}
									pathOptions={{
										fillColor: isSelected ? '#99f6e4' : '#d1d5db',
										fillOpacity: 0.4,
										color: isSelected ? '#0d9488' : '#6b7280',
										weight: 2,
									}}
									eventHandlers={{ click: () => onSiteSelect(site.serialNumber!) }}
								>
									{tooltip}
								</Polygon>
							)}
							{site.location && zoom < 12 && (
								<Marker
									position={[site.location.y, site.location.x]}
									icon={createSiteIcon(isSelected)}
									eventHandlers={{ click: () => onSiteSelect(site.serialNumber!) }}
								>
									<Tooltip direction="top" offset={[0, -8]}>
										<span className="font-medium">{site.name}</span>
										<br />
										<span className="text-gray-500 text-xs">{site.serialNumber}</span>
									</Tooltip>
								</Marker>
							)}
						</Fragment>
					);
				})}

				{fieldPolygons.map(({ field, coords }) => (
					<Polygon
						key={`field-${field.fieldId}`}
						positions={coords.map(c => [c.latitude as number, c.longitude as number] as [number, number])}
						pathOptions={{ color: '#d97706', weight: 1.5, fillColor: '#fbbf24', fillOpacity: 0.12 }}
					>
						<Tooltip sticky>
							<span className="font-medium">{field.fieldName ?? `Field ${field.fieldId}`}</span>
						</Tooltip>
					</Polygon>
				))}

				{plotEntries.map(({ plot, treatmentFriendlyName }) => {
					const pos = centroid(plot.geolocation!);
					if (!pos) {
						return null;
					}
					const isSelected = selectedLocationIds.has(plot.locationId!);
					const sensorCount = (plot.sensors ?? []).length;
					const cap = getPlotCapability(plot);
					const capLabel = cap === 'mixed' ? getPlotCapabilityDetail(plot) : CAPABILITY_COLORS[cap].label;
					const hasT54 = (plot.sensors ?? []).some(s => s.deviceSerialKey?.startsWith('T54'));
					const hasT32 = (plot.sensors ?? []).some(s => s.deviceSerialKey?.startsWith('T32'));
					const chamberNumber = (plot.chamberNumbers ?? []).length > 0
						? plot.chamberNumbers!.join(', ')
						: null;
					return (
						<Marker
							key={`plot-${plot.locationId}`}
							position={pos}
							icon={createPlotIcon(plot, isSelected)}
							eventHandlers={{ click: () => onPlotSelect(plot.locationId!) }}
						>
							<Tooltip direction="top" offset={[0, -5]}>
								<PlotTooltip
									plotName={plot.plotName}
									plotNumber={plot.plotId}
									treatmentNumber={treatmentFriendlyName}
									chamberNumber={chamberNumber}
									hasT54={hasT54}
									hasT32={hasT32}
									nameSuffix={isSelected && <span className="ml-1 text-teal-600">✓</span>}
									extra={
										<>
											<br />
											<span className="text-gray-500 text-xs">{capLabel}</span>
											{sensorCount > 0 && (
												<>
													<br />
													<span className="text-gray-500 text-xs">{sensorCount} sensor{sensorCount !== 1 ? 's' : ''}</span>
												</>
											)}
										</>
									}
								/>
							</Tooltip>
						</Marker>
					);
				})}
			</MapContainer>
			{plotEntries.length > 0 && <MapLegend />}
		</div>
	);
}
